import type { AnalysisResponse, ApiTableResponse, AppSchema, DashboardData, Health, RecordRow, ValidationIssue } from '../types'

const KEY_STORAGE = 'dcs-api-key'

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(message: string, status: number, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export function getApiKey() {
  return sessionStorage.getItem(KEY_STORAGE) || ''
}

export function setApiKey(value: string) {
  if (value) sessionStorage.setItem(KEY_STORAGE, value)
  else sessionStorage.removeItem(KEY_STORAGE)
}

async function request<T>(path: string, init: RequestInit = {}, raw = false): Promise<T> {
  const headers = new Headers(init.headers || {})
  const key = getApiKey()
  if (key) headers.set('X-API-Key', key)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, { ...init, headers })
  if (!response.ok) {
    let detail: any
    try { detail = await response.json() } catch { detail = await response.text() }
    const message = typeof detail?.detail === 'string' ? detail.detail : `HTTP ${response.status}`
    throw new ApiError(message, response.status, detail)
  }
  if (raw) return response as unknown as T
  const text = await response.text()
  return (text ? JSON.parse(text) : {}) as T
}

export const api = {
  health: () => request<Health>('/health'),
  dashboard: () => request<DashboardData>('/api/v1/dashboard'),
  schema: () => request<AppSchema>('/api/v1/schema'),
  validation: () => request<{ issues: ValidationIssue[]; counts: Record<string, number> }>('/api/v1/validation'),
  audit: (limit = 1000) => request<RecordRow[]>(`/api/v1/audit?limit=${limit}`),
  search: (q: string) => request<{ items: RecordRow[] }>(`/api/v1/search?q=${encodeURIComponent(q)}&limit=50`),
  list: (table: string, limit = 500, offset = 0) => request<ApiTableResponse>(`/api/v1/data/${table}?limit=${limit}&offset=${offset}`),
  get: (table: string, keys: Record<string, any>) => request<{ record: RecordRow | null }>(`/api/v1/data/${table}/get`, { method: 'POST', body: JSON.stringify(keys) }),
  save: (table: string, payload: RecordRow) => request<RecordRow>(`/api/v1/data/${table}`, { method: 'POST', body: JSON.stringify(payload) }),
  remove: (table: string, keys: Record<string, any>) => {
    const query = new URLSearchParams(Object.entries(keys).map(([k, v]) => [k, String(v)]))
    return request<{ deleted: boolean; revision: number }>(`/api/v1/data/${table}?${query.toString()}`, { method: 'DELETE' })
  },
  analysis: (kind: 'experiments' | 'phases' | 'temperature-series') => request<AnalysisResponse>(`/api/v1/analysis/${kind}`),
  coverage: (allFeatures = false) => request<{ rows: RecordRow[]; revision: number }>(`/api/v1/analysis/coverage?all_features=${allFeatures}`),
  factors: () => request<{ rows: RecordRow[]; revision: number; warning: string }>('/api/v1/analysis/factors'),
  ml: (target: string) => request<Record<string, any>>(`/api/v1/analysis/ml/${encodeURIComponent(target)}`),
  extractionRuns: (sourceId?: string) => request<{ items: RecordRow[] }>(`/api/v1/extraction/runs${sourceId ? `?source_id=${encodeURIComponent(sourceId)}` : ''}`),
  extractionCandidates: (runId?: string) => request<{ items: RecordRow[] }>(`/api/v1/extraction/candidates${runId ? `?run_id=${encodeURIComponent(runId)}` : ''}`),
  extractionRun: (sourceId: string) => request<RecordRow>(`/api/v1/extraction/${encodeURIComponent(sourceId)}/run`, { method: 'POST', body: JSON.stringify({ prefer_local: true }) }),
  reviewCandidate: (candidateId: string, decision: 'accept' | 'reject') => request<RecordRow>(`/api/v1/extraction/candidates/${encodeURIComponent(candidateId)}/review`, { method: 'POST', body: JSON.stringify({ decision }) }),
  acceptValid: (runId: string) => request<RecordRow>(`/api/v1/extraction/runs/${encodeURIComponent(runId)}/accept-valid`, { method: 'POST' }),
  rollbackRun: (runId: string) => request<RecordRow>(`/api/v1/extraction/runs/${encodeURIComponent(runId)}/rollback`, { method: 'POST' }),
  attachDocument: (sourceId: string, file: File) => request<RecordRow>(`/api/v1/extraction/${encodeURIComponent(sourceId)}/document`, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name) },
    body: file
  }),
  importZip: (file: File) => {
    const fd = new FormData(); fd.append('file', file)
    return request<RecordRow>('/api/v1/import-zip', { method: 'POST', body: fd })
  },
  exportUrl: (kind: 'bundle' | 'analysis.csv') => kind === 'bundle' ? '/api/v1/export/bundle' : '/api/v1/export/analysis.csv',
  async download(path: string, filename: string) {
    const response = await request<Response>(path, {}, true)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }
}
